// 施工箇所(ConstructionPlanArea)新規/編集フォーム。
// scalar(work_id / work_type_id / work_subtype_id / planned_qty / planned_start / planned_end / remarks)に加え、
// 子(場所区分 locations / 計画材料 planned_materials)も子込み route(construction_plan_area.put/patch)で一括保存する。
// construction_plan_id は edit では既存値、new では同一工事の既存施工箇所から継承する。
// 親子とも hasMerge:true でオフライン保存に対応済み(construction_plan_area.put/patch がオフライン時・送信失敗時に
// ConstructionPlanAreaDataApi へフォールバックし、IDB上でdelete-insert意味論を再現してdirty行を積む)。
// OfflineMasterBanner はマスタ(工事/場所区分/材料 等)がキャッシュ由来であることの注意喚起として残す
// (保存自体はオフラインで可能だが、参照するマスタが最新でない可能性があるため)。
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Stack, Typography, Paper, Button, TextField, MenuItem, Alert, Divider, IconButton,
  Autocomplete, Table, TableHead, TableBody, TableRow, TableCell, Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import { errorMessageOf, required } from "../../../util/typed";
import { useFullDb } from "../../../api/dbSlice";
import { useIsOnline } from "../../../util/useIsOnline";
import { useMountSync } from "../../../util/useMountSync";
import { ContentLoading } from "../../../components/Loading";
import { OfflineMasterBanner } from "../../../components/OfflineMasterBanner";
import { epochToDateInput, dateInputToEpoch } from "../../../util/dateInput";
import { mixRatioDisplayName } from "../../../util/mixRatioLabel";
import { deriveProcMatRows, mainDetailUnitOfWorkDetail, mainMaterialIdOfWorkDetail } from "./areaFormHelpers";

type Mode = "new" | "edit";

// 計画材料の編集行。key は React の安定キー(行追加/削除でフォーカスを保つため)。
interface MatRow {
  key: number;
  materialId: number | "";
  qty: string;
  unit: string;
}

function fmtQty(n: number | null): string {
  if (n == null) {
    return "-";
  }
  return Number(n.toFixed(2)).toString();
}

export default function AreaFormPage({ pageContext, mode }: { pageContext: PageContext; mode: Mode }) {
  const { areaId } = useParams<{ areaId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { db, loading, reload } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);
  const isOnline = useIsOnline(pageContext.api);

  const existing = useMemo(() => (mode === "edit" && areaId ? db.constructionPlanAreas.find((a) => a.id === areaId) ?? null : null), [mode, areaId, db.constructionPlanAreas]);

  const queryWorkId = searchParams.get("work_id");
  const initialWorkId = existing?.work_id ?? (queryWorkId != null ? Number(queryWorkId) : null);

  const [workId, setWorkId] = useState<number | null>(initialWorkId);
  const [workTypeId, setWorkTypeId] = useState<number | null>(existing?.work_type_id ?? (searchParams.get("work_type_id") != null ? Number(searchParams.get("work_type_id")) : null));
  const [workSubtypeId, setWorkSubtypeId] = useState<number | null>(existing?.work_subtype_id ?? (searchParams.get("work_subtype_id") != null ? Number(searchParams.get("work_subtype_id")) : null));
  const [plannedQty, setPlannedQty] = useState<string>(existing?.planned_qty != null ? String(existing.planned_qty) : "");
  const [plannedStart, setPlannedStart] = useState<string>(epochToDateInput(existing?.planned_start ?? null));
  const [plannedEnd, setPlannedEnd] = useState<string>(epochToDateInput(existing?.planned_end ?? null));
  const [remarks, setRemarks] = useState<string>(existing?.remarks ?? "");
  // 場所区分: slot → location_division_id。未選択の slot はキーを持たない。
  const [slotDivs, setSlotDivs] = useState<Record<number, number>>({});
  // 工程由来の主材料計画数量: work_detail_id → kg。未入力の工程はキーを持たない。
  const [plannedMainQtyMap, setPlannedMainQtyMap] = useState<Record<number, number>>({});
  // 追加材料(手入力): 配合比未設定の工程や任意材料向け。保存時 work_detail_id=null。
  const [matRows, setMatRows] = useState<MatRow[]>([]);
  // 旧 null 行のうち複数工程で曖昧なため工程へ割り当てられず、材料単位で参考表示するもの(material_id → 合計)。
  const [legacyMaterialQtyMap, setLegacyMaterialQtyMap] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const nextRowKey = useRef(1);

  // edit を直リンクで開いた場合、IDBに親(ConstructionPlanArea)はあっても子(locations/plannedMaterials)が
  // 未キャッシュのことがある。子が空のまま hydrate すると、保存時の delete-insert で既存の子がサーバから
  // 消えてしまうため、オンラインなら子込みでサーバー同期してから hydrate する。
  const syncing = useMountSync(mode === "edit" && isOnline, () => pageContext.api.entities.syncConstructionData(), reload);

  // edit 時は DB 読み込み完了(loading=false)かつ上記同期完了(syncing=false)で
  // existing が入った段階でフォームを一度だけ hydrate する
  // (useState 初期値は初回 render の existing=null 時点で確定するため、これが無いと編集画面が空になる)。
  // syncing 完了前に existing が(旧キャッシュから)見えていても hydrate しない。
  const hydrated = useRef(false);
  useEffect(() => {
    if (mode === "edit" && existing !== null && !hydrated.current && !loading && !syncing) {
      hydrated.current = true;
      setWorkId(existing.work_id);
      setWorkTypeId(existing.work_type_id);
      setWorkSubtypeId(existing.work_subtype_id);
      setPlannedQty(existing.planned_qty != null ? String(existing.planned_qty) : "");
      setPlannedStart(epochToDateInput(existing.planned_start));
      setPlannedEnd(epochToDateInput(existing.planned_end));
      setRemarks(existing.remarks ?? "");

      const sd: Record<number, number> = {};
      for (const l of db.constructionPlanAreaLocations) {
        if (l.deleted_at == null && l.construction_plan_area_id === existing.id) {
          sd[l.slot] = l.location_division_id;
        }
      }
      setSlotDivs(sd);

      // 既存計画材料を新方式へ振り分ける(設計書§6)。
      //   1. work_detail_id 付き行 → 該当工程の主材料計画数量へ。
      //   2. 旧 null 行 → その材料を主材料とする工程が一意なら該当工程へ、複数なら手入力へ残し材料単位で参考表示。
      const subtypeId = existing.work_subtype_id;
      const subtypeWds = subtypeId == null
        ? []
        : db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === subtypeId);
      const wdByMaterial = new Map<number, number[]>();
      for (const swd of subtypeWds) {
        const swdMid = mainMaterialIdOfWorkDetail(db, swd.id);
        if (swdMid != null) {
          const arr = wdByMaterial.get(swdMid) ?? [];
          arr.push(swd.id);
          wdByMaterial.set(swdMid, arr);
        }
      }

      const mainQtyMap: Record<number, number> = {};
      const legacyMap: Record<number, number> = {};
      const manualRows: MatRow[] = [];
      for (const p of db.constructionPlanAreaPlannedMaterials) {
        if (p.deleted_at != null || p.construction_plan_area_id !== existing.id) {
          continue;
        }
        const wdId = p.work_detail_id ?? null;
        if (wdId != null) {
          mainQtyMap[wdId] = p.planned_qty;
          continue;
        }
        const owningWds = wdByMaterial.get(p.material_id) ?? [];
        const soleOwner = owningWds[0];
        if (owningWds.length === 1 && soleOwner !== undefined) {
          mainQtyMap[soleOwner] = p.planned_qty;
        } else {
          if (owningWds.length > 1) {
            legacyMap[p.material_id] = (legacyMap[p.material_id] ?? 0) + p.planned_qty;
          }
          manualRows.push({ key: nextRowKey.current, materialId: p.material_id, qty: String(p.planned_qty), unit: p.unit });
          nextRowKey.current += 1;
        }
      }
      setPlannedMainQtyMap(mainQtyMap);
      setLegacyMaterialQtyMap(legacyMap);
      setMatRows(manualRows);
    }
  }, [mode, existing, db, loading, syncing]);

  const activeWorks = db.works.filter((w) => w.deleted_at == null);
  const selectedWork = workId == null ? undefined : db.works.find((w) => w.id === workId);
  const areaDivisionId = selectedWork?.area_division_id ?? null;
  const workTypeChoices = db.workTypes.filter((t) => t.deleted_at == null && (selectedWork == null || t.area_division_id === selectedWork.area_division_id));
  const workSubtypeChoices = db.workSubtypes.filter((s) => s.deleted_at == null && (workTypeId == null || s.work_type_id === workTypeId));
  const activeMaterials = useMemo(() => db.materials.filter((m) => m.deleted_at == null).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)), [db.materials]);

  // 種別配下の工程プレビュー
  const workDetailOptions = useMemo(() => (
    workSubtypeId == null
      ? []
      : db.workDetails.filter((d) => d.deleted_at == null && d.work_subtype_id === workSubtypeId)
  ), [db.workDetails, workSubtypeId]);

  // 計画材料行の検証。patch は planned_materials を「置換」するため、不完全な行(材料のみ/数量のみ)を黙って除外すると
  // 既存材料が意図せず削除される。完全な行(材料 + 0以上の数量)か、完全な空行のみ許可し、材料の重複も禁止する。
  const materialRowError = useMemo<string | null>(() => {
    const seen = new Set<number>();
    for (const r of matRows) {
      const hasMat = r.materialId !== "";
      const hasQty = r.qty !== "";
      const qtyNum = Number(r.qty);
      const validQty = hasQty && !Number.isNaN(qtyNum) && qtyNum >= 0;
      const bothEmpty = !hasMat && !hasQty;
      const complete = hasMat && validQty;
      if (!bothEmpty && !complete) {
        return "計画材料の各行は「材料」と「計画数量(0以上の数値)」を両方入力してください(不要な行は削除)。";
      }
      if (complete && typeof r.materialId === "number") {
        if (seen.has(r.materialId)) {
          return "同じ材料が複数行にあります。1材料につき1行にまとめてください。";
        }
        seen.add(r.materialId);
      }
    }
    return null;
  }, [matRows]);

  // 場所区分スロット: areaDivisionLocationLabels でラベル/必須を取得、locationDivisions で候補を取得
  const locationSlots = useMemo<{ slot: number; label_name: string; is_required: boolean; choices: { id: number; division_name: string }[] }[]>(() => {
    if (areaDivisionId == null) {
      return [];
    }
    const labels = db.areaDivisionLocationLabels.filter((l) => l.deleted_at == null && l.area_division_id === areaDivisionId);
    const divs = db.locationDivisions.filter((d) => d.deleted_at == null && d.area_division_id === areaDivisionId);
    const slotSet = new Set<number>();
    for (const label of labels) { slotSet.add(label.slot); }
    for (const div of divs) { slotSet.add(div.slot); }
    const slots = Array.from(slotSet).sort((a, b) => a - b);
    // 上位区分パス(locationDivisionParentPaths)による下位スロット候補の絞り込み。
    // 候補にパス制約があり、その上位スロットで選択済みの区分名と一致しない場合のみ除外する(未選択・制約なし=絞り込まない)。
    const activePaths = db.locationDivisionParentPaths.filter((p) => p.deleted_at == null);
    const isChoiceAllowed = (d: { id: number; slot: number }) => {
      const constraints = activePaths.filter((p) => p.location_division_id === d.id && p.ancestor_slot < d.slot);
      const conflicting = constraints.find((c) => {
        const selectedId = slotDivs[c.ancestor_slot];
        if (selectedId == null) {
          return false;
        }
        const selected = divs.find((x) => x.id === selectedId);
        if (selected == null) {
          return false;
        }
        return selected.division_name !== c.ancestor_division_name;
      });
      return conflicting == null;
    };
    return slots.map((slot) => {
      const labelEntry = labels.find((l) => l.slot === slot);
      const choices = divs.filter((d) => d.slot === slot).filter(isChoiceAllowed).sort((a, b) => a.display_order - b.display_order);
      return {
        slot,
        label_name: labelEntry?.label_name ?? `区分${slot}`,
        is_required: labelEntry?.is_required ?? false,
        choices: choices.map((d) => ({ id: d.id, division_name: d.division_name })),
      };
    });
  }, [db.areaDivisionLocationLabels, db.locationDivisions, db.locationDivisionParentPaths, areaDivisionId, slotDivs]);

  // new 時の construction_plan_id を同一工事の既存施工箇所から継承する。
  const inheritedPlanId = useMemo(() => {
    if (mode !== "new" || workId == null) {
      return null;
    }
    const sibling = db.constructionPlanAreas.find((a) => a.deleted_at == null && a.work_id === workId);
    return sibling?.construction_plan_id ?? null;
  }, [mode, workId, db.constructionPlanAreas]);

  // オフラインで edit を開いた際、対象エリアの子(場所区分/計画材料)が IDB に1件も無い場合は
  // 「未同期でキャッシュが空なだけ」なのか「本当に空」なのか区別できない。誤って空へ上書き
  // (delete-insert)しないよう、子の編集を保護する。
  const hasAnyChildCached = useMemo(() => {
    if (mode !== "edit" || existing == null) {
      return true;
    }
    const hasLocations = db.constructionPlanAreaLocations.some((l) => l.construction_plan_area_id === existing.id);
    const hasMaterials = db.constructionPlanAreaPlannedMaterials.some((p) => p.construction_plan_area_id === existing.id);
    return hasLocations || hasMaterials;
  }, [mode, existing, db.constructionPlanAreaLocations, db.constructionPlanAreaPlannedMaterials]);
  const offlineChildEditRisk = mode === "edit" && !isOnline && !hasAnyChildCached;

  if (loading || syncing) {
    return (
      <Stack p={3} gap={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/console/construction-plan/")} sx={{ alignSelf: "flex-start" }}>戻る</Button>
        <ContentLoading />
      </Stack>
    );
  }
  if (mode === "edit" && existing === null) {
    return (
      <Stack p={3} gap={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/console/construction-plan/")} sx={{ alignSelf: "flex-start" }}>戻る</Button>
        <Typography color="text.secondary">施工箇所が見つかりません。</Typography>
      </Stack>
    );
  }

  // slot → location_division_id の更新。文字列からマスタを逆引きしてIDを決定する。
  const setSlotByName = (slot: number, divisionName: string) => {
    const slotInfo = locationSlots.find((s) => s.slot === slot);
    const matched = slotInfo?.choices.find((c) => c.division_name === divisionName);
    setSlotDivs((prev) => {
      const next = { ...prev };
      if (matched != null) {
        next[slot] = matched.id;
      } else {
        delete next[slot];
      }
      return next;
    });
  };

  const addMatRow = () => {
    setMatRows((prev) => [...prev, { key: nextRowKey.current, materialId: "", qty: "", unit: "" }]);
    nextRowKey.current += 1;
  };
  const removeMatRow = (key: number) => setMatRows((prev) => prev.filter((r) => r.key !== key));
  const updateMatRow = (key: number, patch: Partial<MatRow>) => setMatRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const planId = existing?.construction_plan_id ?? inheritedPlanId;

  // 施工計画(ConstructionPlan)の解決。sibling 継承できない初回は、既存計画の再利用 → 無ければ自動作成する。
  // mock では計画はユーザー概念から廃止済み(施工箇所が計画の実体)で、正規化スキーマの必須FKを満たすための
  // 合成ヘッダ行にすぎないため、ここで自動採番してユーザーには露出しない。
  const resolvePlanId = async (): Promise<string> => {
    if (planId != null) {
      return planId;
    }
    const api = pageContext.api;
    // 選択済みの工事(Work)配下の施工計画を再利用し、無ければ合成ヘッダ行を自動作成する。
    // workId は save() 手前の validate() で非 null が保証される。
    const selectedWorkId = required(workId, "選択中の工事");
    const plans = await api.construction_plan.getFromWorkId({ id: selectedWorkId });
    const activePlan = plans.find((p) => p.deleted_at == null);
    if (activePlan != null) {
      return activePlan.id;
    }
    const created = await api.construction_plan.put({
      work_id: selectedWorkId,
      project_type: "", work_type: "", work_subcategory: "",
      execution_method: "", mixing_method: "",
      plan_info_1: "", plan_info_2: "", plan_info_3: "", plan_info_4: "",
      plan_info_5: "", plan_info_6: "", plan_info_7: "", plan_info_8: "",
    });
    return created.id;
  };

  // フォームバリデーション(mockの文言に合わせる)
  const validate = (): string | null => {
    if (workId == null) { return "工事を選択してください"; }
    if (workTypeId == null) { return "工種を選択してください"; }
    if (workSubtypeId == null) { return "種別を選択してください"; }
    if (workDetailOptions.length === 0) { return "選択した種別に配下の工程 (工程マスタ) が登録されていません。マスタを先に整備してください"; }
    const missingRequired = locationSlots.filter((ls) => ls.is_required && slotDivs[ls.slot] == null);
    if (missingRequired.length > 0) { return `必須の場所区分が未入力です: ${missingRequired.map((ls) => ls.label_name).join(", ")}`; }
    if (materialRowError != null) { return materialRowError; }
    return null;
  };

  const canSave = !saving;

  const buildChildren = () => {
    const locations = locationSlots.flatMap((ls) => {
      const divisionId = slotDivs[ls.slot];
      if (divisionId == null) {
        return [];
      }
      return [{ location_division_id: divisionId, slot: ls.slot }];
    });

    // 工程由来の主材料行(数量 > 0 のみ)。material_id + work_detail_id で工程別に分離する。
    const processMaterials: { material_id: number; work_detail_id: number; planned_qty: number; unit: string }[] = [];
    for (const wdIdStr of Object.keys(plannedMainQtyMap)) {
      const wdId = Number(wdIdStr);
      const qty = plannedMainQtyMap[wdId];
      if (qty == null || !(qty > 0)) {
        continue;
      }
      const materialId = mainMaterialIdOfWorkDetail(db, wdId);
      if (materialId == null) {
        continue;
      }
      processMaterials.push({
        material_id: materialId,
        work_detail_id: wdId,
        planned_qty: qty,
        unit: mainDetailUnitOfWorkDetail(db, wdId),
      });
    }

    // 追加材料(手入力)行は work_detail_id=null で保存する。
    const manualMaterials = matRows.flatMap((r) => {
      if (typeof r.materialId !== "number" || r.qty === "" || Number.isNaN(Number(r.qty))) {
        return [];
      }
      return [{ material_id: r.materialId, work_detail_id: null, planned_qty: Number(r.qty), unit: r.unit }];
    });

    return { locations, planned_materials: [...processMaterials, ...manualMaterials] };
  };

  const save = async () => {
    const err = validate();
    if (err != null) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    setSaving(true);
    try {
      const resolvedPlanId = await resolvePlanId();
      const scalar = {
        work_id: workId,
        work_type_id: workTypeId,
        work_subtype_id: workSubtypeId,
        planned_qty: plannedQty === "" ? null : Number(plannedQty),
        planned_start: dateInputToEpoch(plannedStart),
        planned_end: dateInputToEpoch(plannedEnd),
        remarks: remarks === "" ? null : remarks,
      };
      const children = buildChildren();
      if (mode === "edit" && existing) {
        // オフラインで子(場所区分/計画材料)が未同期の可能性がある場合は子を送信せず、
        // 親スカラーのみ patch する(子の delete-insert を発動させない)。
        const childrenToSend = offlineChildEditRisk ? { locations: undefined, planned_materials: undefined } : children;
        await pageContext.api.construction_plan_area.patch({
          id: existing.id,
          construction_plan_id: existing.construction_plan_id,
          area_info_1: existing.area_info_1,
          area_info_2: existing.area_info_2,
          area_info_3: existing.area_info_3,
          area_info_4: existing.area_info_4,
          area_info_5: existing.area_info_5,
          ...scalar,
          ...childrenToSend,
        });
        navigate("/console/construction-plan/");
      } else {
        await pageContext.api.construction_plan_area.put({
          construction_plan_id: resolvedPlanId,
          area_info_1: "", area_info_2: "", area_info_3: "", area_info_4: "", area_info_5: "",
          ...scalar,
          ...children,
        });
        navigate("/console/construction-plan/");
      }
    } catch (e) {
      pageContext.api.showError(`保存に失敗しました: ${errorMessageOf(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const selectedSubtype = workSubtypeId != null ? workSubtypeChoices.find((s) => s.id === workSubtypeId) : undefined;
  const subtypeUnit = selectedSubtype?.unit ?? "";

  const procMatRows = deriveProcMatRows(db, workSubtypeId, plannedMainQtyMap);

  const setMainQty = (workDetailId: number, qty: number | null) => {
    setPlannedMainQtyMap((prev) => {
      const next = { ...prev };
      if (qty == null) {
        delete next[workDetailId];
      } else {
        next[workDetailId] = qty;
      }
      return next;
    });
  };

  // 手入力へ残った旧 null 行の参考表示用: material_id → 材料名。
  const legacyMaterialNames = Object.keys(legacyMaterialQtyMap).map((idStr) => {
    const mid = Number(idStr);
    const mat = db.materials.find((m) => m.id === mid);
    return { materialId: mid, name: mat?.name ?? `材料#${mid}`, qty: legacyMaterialQtyMap[mid] };
  });

  return (
    <Stack p={3} gap={3} maxWidth={900} mx="auto">
      <Typography variant="h4" fontWeight="bold">
        {mode === "edit" ? "施工箇所の編集" : "施工箇所"}
      </Typography>

      <OfflineMasterBanner isOnline={isOnline} />

      {offlineChildEditRisk && (
        <Alert severity="warning">
          オフラインのため区画・計画材料は編集できません(未同期の可能性)。オンラインで一度開くと編集可能になります。
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2.5}>

          {/* ① 場所区分の入力 */}
          <Typography variant="subtitle1" fontWeight="bold">場所区分の入力</Typography>
          <Alert severity="info" sx={{ py: 0.5 }}>
            候補は「場所区分マスタ」から自動で引いています。上位の区分を選ぶと配下の候補が絞り込まれます。候補にない場所は自由入力できます（表記揺れに注意）。
            {workId == null && " 工事を選択すると、ラベルと候補がその工事の工事型区分に応じて更新されます。"}
          </Alert>
          {areaDivisionId == null ? (
            <Typography variant="body2" color="text.secondary">工事を選択すると場所区分を指定できます。</Typography>
          ) : locationSlots.length === 0 ? (
            <Typography variant="body2" color="text.secondary">この工事の区域区分には場所区分が登録されていません。</Typography>
          ) : (
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 2,
            }}>
              {locationSlots.map((ls) => {
                const opts = ls.choices.map((c) => c.division_name);
                const currentId = slotDivs[ls.slot];
                const currentName = currentId != null ? (ls.choices.find((c) => c.id === currentId)?.division_name ?? "") : "";
                const hasAlias = ls.label_name !== `区分${ls.slot}`;
                const labelText = hasAlias
                  ? `${ls.label_name}${ls.is_required ? " *" : ""}`
                  : `区分${ls.slot}: ${ls.label_name}${ls.is_required ? " *" : ""}`;
                const helperText = opts.length > 0
                  ? `候補 ${opts.length}件`
                  : "候補マスタなし（自由入力）";
                return (
                  <Autocomplete key={ls.slot}
                    freeSolo
                    disabled={offlineChildEditRisk}
                    options={opts}
                    value={currentName}
                    onChange={(_, v) => setSlotByName(ls.slot, v ?? "")}
                    onInputChange={(_, v) => setSlotByName(ls.slot, v)}
                    renderInput={(params) => (
                      <TextField {...params}
                        label={labelText}
                        required={ls.is_required}
                        helperText={helperText}
                        size="small"
                      />
                    )}
                  />
                );
              })}
            </Box>
          )}

          {/* ② 工事・工種・種別 */}
          <Divider />
          <Typography variant="subtitle1" fontWeight="bold">工事・工種・種別</Typography>
          <TextField select label="工事" size="small" value={workId ?? ""} disabled={mode === "edit"}
            onChange={(e) => {
              setWorkId(e.target.value === "" ? null : Number(e.target.value));
              setWorkTypeId(null);
              setWorkSubtypeId(null);
              setSlotDivs({});
              setPlannedMainQtyMap({});
              setLegacyMaterialQtyMap({});
            }}>
            <MenuItem value="">未選択</MenuItem>
            {activeWorks.map((w) => <MenuItem key={w.id} value={w.id}>{w.short_name || w.official_name}</MenuItem>)}
          </TextField>
          <TextField select label="工種" size="small" value={workTypeId ?? ""}
            onChange={(e) => {
              setWorkTypeId(e.target.value === "" ? null : Number(e.target.value));
              setWorkSubtypeId(null);
              setPlannedMainQtyMap({});
              setLegacyMaterialQtyMap({});
            }}>
            <MenuItem value="">未選択</MenuItem>
            {workTypeChoices.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <TextField select label="種別" size="small" value={workSubtypeId ?? ""}
            onChange={(e) => {
              setWorkSubtypeId(e.target.value === "" ? null : Number(e.target.value));
              setPlannedMainQtyMap({});
              setLegacyMaterialQtyMap({});
            }}
            helperText="種別を変更すると、配下工程が下のテーブルに自動表示されます。工程と配合比は工種マスタ側で管理されており、ここでは表示のみです。">
            <MenuItem value="">未選択</MenuItem>
            {workSubtypeChoices.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>

          {/* ③ 予定数量・期間 */}
          <Divider />
          <Typography variant="subtitle1" fontWeight="bold">予定数量・期間</Typography>
          <Stack direction="row" gap={2}>
            <TextField label={subtypeUnit ? `予定施工数量 (${subtypeUnit})` : "予定施工数量"} size="small" type="number"
              value={plannedQty} onChange={(e) => setPlannedQty(e.target.value)} sx={{ flex: 1 }}
              helperText={workSubtypeId != null
                ? `単位は種別「${selectedSubtype?.name ?? ""}」から自動 → ${subtypeUnit || "(未設定)"}`
                : "種別を選ぶと単位が自動表示されます"} />
            <TextField label="開始予定" size="small" type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            <TextField label="終了予定" size="small" type="date" value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          </Stack>

          {/* ④ 工程 */}
          <Divider />
          <Typography variant="subtitle1" fontWeight="bold">工程</Typography>
          <Alert severity="info" sx={{ py: 0.5 }}>
            工程の構成 (素地調整 / 改質材塗布 / プライマー塗布 / 主材兼上塗り など) と配合比は<strong>工種マスタ</strong>側で管理されます。ここではプレビューのみ表示します。
          </Alert>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: 40 }}>順</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>配合比</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>使用材料</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workDetailOptions.sort((a, b) => a.id - b.id).map((wd, idx) => {
                  const mix = wd.mix_ratio_definition_id != null
                    ? db.mixRatioDefinitions.find((x) => x.id === wd.mix_ratio_definition_id)
                    : undefined;
                  const matId = mix?.material_id;
                  const mat = matId != null ? db.materials.find((m) => m.id === matId) : undefined;
                  return (
                    <TableRow key={wd.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{wd.name}</TableCell>
                      <TableCell>{mix ? mixRatioDisplayName(db, mix) : "(なし)"}</TableCell>
                      <TableCell>{mat?.name ?? "-"}</TableCell>
                    </TableRow>
                  );
                })}
                {workDetailOptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2, color: "text.secondary" }}>
                      種別を選択すると、配下の工程が表示されます
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* ⑤ 使用材料一覧(工程ベース: 主材料は工程ごとに入力、従属材料は配合比から自動計算) */}
          <Divider />
          <Typography variant="h6" fontWeight="bold">使用材料一覧</Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>主材料の計画数量は工程ごとに入力</strong>してください。<strong>従属材料</strong>は配合比から自動計算されます。標準使用量が登録されている材料は入力欄の下にメーカー参考値として表示します。
          </Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>材料</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>細目</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 80 }}>役割</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">配合比</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">計画数量</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: 60 }}>単位</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {procMatRows.map((r) => (
                  <TableRow key={r.rowKey} sx={{ backgroundColor: r.isMain ? "rgba(25, 118, 210, 0.04)" : undefined }}>
                    <TableCell>{r.isMain ? r.workDetailName : ""}</TableCell>
                    <TableCell>{r.materialName}</TableCell>
                    <TableCell>{r.detailName}</TableCell>
                    <TableCell>
                      {r.isMain
                        ? <Chip size="small" color="primary" label="主材料" />
                        : <Chip size="small" variant="outlined" label="従属" />}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" component="span">{r.ratioText}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {r.isMain ? (
                        <Stack alignItems="flex-end" gap={0.25}>
                          <TextField type="number" size="small" sx={{ width: 100 }}
                            disabled={offlineChildEditRisk}
                            value={plannedMainQtyMap[r.workDetailId] ?? ""}
                            onChange={(e) => setMainQty(r.workDetailId, e.target.value === "" ? null : Number(e.target.value))}
                            inputProps={{ min: 0, step: "any", style: { textAlign: "right" } }} />
                          {r.referenceStandardUsage != null && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, lineHeight: 1.2 }}>
                              参考: {r.referenceStandardUsage}{r.referenceStandardUsageUnit ?? ""}
                            </Typography>
                          )}
                          {r.materialRemarks != null && r.materialRemarks !== "" && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, lineHeight: 1.2, fontStyle: "italic" }}>
                              {r.materialRemarks}
                            </Typography>
                          )}
                        </Stack>
                      ) : r.qty == null ? (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      ) : r.qtyMax != null && r.qtyMin != null && r.qtyMax !== r.qtyMin ? (
                        <Typography variant="body2">{fmtQty(r.qtyMin)}〜{fmtQty(r.qtyMax)}</Typography>
                      ) : (
                        <Typography variant="body2">{fmtQty(r.qty)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{r.detailUnit}</TableCell>
                  </TableRow>
                ))}
                {procMatRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      配合比が紐付く工程がありません。工程マスタの整備後に主材料の計画数量を入力できます。
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {legacyMaterialNames.length > 0 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              旧方式(材料単位)で登録された計画数量のうち、複数工程で同じ材料を使うため工程へ自動割当できないものがあります。下の手入力欄に残しています。工程別の計画数量を上の表へ入力し直すと整理されます。
              <Box component="ul" sx={{ my: 0.5, pl: 2 }}>
                {legacyMaterialNames.map((l) => (
                  <li key={l.materialId}>{l.name}: {fmtQty(l.qty ?? null)}</li>
                ))}
              </Box>
            </Alert>
          )}

          {/* 手入力(補助): 配合比未設定の工程や任意材料向け。保存時 work_detail_id=null。 */}
          <Typography variant="subtitle2" fontWeight="bold">手入力の計画材料(補助)</Typography>
          <Typography variant="caption" color="text.secondary">
            配合比が紐付かない工程や、上表に現れない任意材料をここで登録します。工程には紐付かず材料単位で保存されます。
          </Typography>
          <Stack gap={1.5}>
            {matRows.length === 0 && <Typography variant="body2" color="text.secondary">手入力の計画材料は未設定です。</Typography>}
            {matRows.map((r) => (
              <Stack key={r.key} direction="row" gap={1.5} alignItems="center">
                <TextField select size="small" label="材料" sx={{ flex: 2, minWidth: 200 }}
                  disabled={offlineChildEditRisk}
                  value={r.materialId === "" ? "" : r.materialId}
                  onChange={(e) => {
                    const v: number | "" = e.target.value === "" ? "" : Number(e.target.value);
                    const mat = v === "" ? undefined : db.materials.find((m) => m.id === v);
                    updateMatRow(r.key, { materialId: v, unit: r.unit || mat?.standard_usage_unit || "" });
                  }}>
                  <MenuItem value="">(選択)</MenuItem>
                  {activeMaterials.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </TextField>
                <TextField size="small" label="計画数量" type="number" sx={{ flex: 1, minWidth: 110 }}
                  disabled={offlineChildEditRisk}
                  value={r.qty} onChange={(e) => updateMatRow(r.key, { qty: e.target.value })} />
                <TextField size="small" label="単位" sx={{ width: 90 }}
                  disabled={offlineChildEditRisk}
                  value={r.unit} onChange={(e) => updateMatRow(r.key, { unit: e.target.value })} />
                <IconButton size="small" onClick={() => removeMatRow(r.key)} disabled={offlineChildEditRisk} aria-label="削除"><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addMatRow} disabled={offlineChildEditRisk} sx={{ alignSelf: "flex-start" }}>計画材料を追加</Button>
          </Stack>

          {/* ⑥ 備考 */}
          <Divider />
          <TextField label="備考" size="small" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />

          {(validationError != null || materialRowError != null) && (
            <Alert severity="error">{validationError ?? materialRowError}</Alert>
          )}

          {/* フッタ */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
            <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate("/console/construction-plan/")}>
              一覧に戻る
            </Button>
            <Button variant="contained" size="large" onClick={save} disabled={!canSave}
              sx={{ minWidth: 200 }}>
              {saving ? "保存中…" : mode === "edit" ? "更新" : "追加する"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
